import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useRehearsalStore } from "@/stores/useRehearsalStore";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import {
  X,
  Play,
  Pause,
  RotateCcw,
  Repeat,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Music2,
  Volume2,
  Minus,
  Plus,
  Hand,
  Clock
} from "lucide-react";
import { toast } from "sonner";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RehearsalPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const audioContextRef = useRef(null);
  const nextBeatTimeRef = useRef(0);
  const timerIdRef = useRef(null);
  
  // Global store
  const {
    currentSongIndex,
    currentSectionIndex,
    bpm,
    isPlaying,
    volume,
    beatsPerBar,
    currentBeat,
    isLooping,
    loopSectionIndex,
    setCurrentSetlist,
    setCurrentSong,
    setCurrentSection,
    setBpm,
    adjustBpm,
    setPlaying,
    setVolume,
    incrementBeat,
    resetBeat,
    toggleLoop,
    tapTempo,
    endSession,
    getSessionStats
  } = useRehearsalStore();
  
  const [setlist, setSetlist] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Countdown state (local, not shared)
  const [countdown, setCountdown] = useState(0);
  const [showCountdown, setShowCountdown] = useState(false);
  
  // Session stats
  const [sessionTime, setSessionTime] = useState(0);
  const currentSong = setlist?.songs?.[currentSongIndex];
  const currentKey = currentSong?.transposed_key || currentSong?.original_key;
  const currentSection = currentSong?.sections?.[currentSectionIndex];
  const beatInBar = currentBeat % beatsPerBar;

  const fetchSetlist = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/setlists/${id}`, {
        headers: getAuthHeaders()
      });
      setSetlist(response.data);
      setCurrentSetlist(id);
      
      // Set initial BPM from first song
      if (response.data.songs?.length > 0) {
        const firstSong = response.data.songs[0];
        if (firstSong.bpm) setBpm(firstSong.bpm);
      }
    } catch (error) {
      toast.error("Error al cargar el setlist");
      navigate("/setlists");
    } finally {
      setLoading(false);
    }
  }, [id, getAuthHeaders, navigate, setCurrentSetlist, setBpm]);

  useEffect(() => {
    fetchSetlist();
    
    // Initialize audio context
    audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    
    return () => {
      if (timerIdRef.current) clearTimeout(timerIdRef.current);
      if (audioContextRef.current) audioContextRef.current.close();
      endSession();
    };
  }, [fetchSetlist, endSession]);

  // Session timer
  useEffect(() => {
    let interval;
    if (isPlaying) {
      interval = setInterval(() => {
        const stats = getSessionStats();
        if (stats) setSessionTime(stats.duration);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isPlaying, getSessionStats]);

  // Metronome sound with accent differentiation
  const playClick = useCallback((time, beatNum) => {
    if (!audioContextRef.current) return;
    
    const ctx = audioContextRef.current;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    
    osc.connect(gain);
    gain.connect(ctx.destination);
    
    // Different sounds for beat 1 (accent) vs others
    const isAccent = beatNum === 0;
    osc.frequency.value = isAccent ? 1200 : 800;
    gain.gain.value = (volume / 100) * (isAccent ? 0.4 : 0.2);
    
    osc.start(time);
    gain.gain.exponentialRampToValueAtTime(0.001, time + 0.05);
    osc.stop(time + 0.05);
  }, [volume]);

  // Metronome scheduler
  const scheduler = useCallback(() => {
    if (!audioContextRef.current || !isPlaying) return;
    
    const ctx = audioContextRef.current;
    const scheduleAheadTime = 0.1;
    const lookahead = 25;
    
    while (nextBeatTimeRef.current < ctx.currentTime + scheduleAheadTime) {
      const beatInBar = currentBeat % beatsPerBar;
      playClick(nextBeatTimeRef.current, beatInBar);
      
      incrementBeat();
      
      const secondsPerBeat = 60.0 / bpm;
      nextBeatTimeRef.current += secondsPerBeat;
    }
    
    timerIdRef.current = setTimeout(scheduler, lookahead);
  }, [isPlaying, bpm, beatsPerBar, currentBeat, playClick, incrementBeat]);

  // Start/stop metronome
  useEffect(() => {
    if (isPlaying) {
      if (audioContextRef.current?.state === "suspended") {
        audioContextRef.current.resume();
      }
      nextBeatTimeRef.current = audioContextRef.current?.currentTime || 0;
      scheduler();
    } else {
      if (timerIdRef.current) {
        clearTimeout(timerIdRef.current);
        timerIdRef.current = null;
      }
    }
    
    return () => {
      if (timerIdRef.current) clearTimeout(timerIdRef.current);
    };
  }, [isPlaying, scheduler]);

  // Countdown before start
  const startWithCountdown = useCallback(() => {
    setShowCountdown(true);
    setCountdown(4);
    
    const countInterval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countInterval);
          setShowCountdown(false);
          resetBeat();
          setPlaying(true);
          return 0;
        }
        playClick(audioContextRef.current?.currentTime || 0, prev === 4 ? 0 : 1);
        return prev - 1;
      });
    }, 60000 / bpm);
  }, [bpm, playClick, resetBeat, setPlaying]);

  const togglePlayHandler = useCallback(() => {
    if (isPlaying) {
      setPlaying(false);
    } else {
      startWithCountdown();
    }
  }, [isPlaying, setPlaying, startWithCountdown]);

  const stopAndReset = useCallback(() => {
    setPlaying(false);
    resetBeat();
  }, [resetBeat, setPlaying]);

  const handleTapTempo = useCallback(() => {
    const newBpm = tapTempo();
    if (newBpm) {
      toast.success(`BPM: ${newBpm}`, { duration: 1000 });
    }
  }, [tapTempo]);

  const goToSong = useCallback((index) => {
    if (!setlist?.songs || index < 0 || index >= setlist.songs.length) return;
    
    setPlaying(false);
    resetBeat();
    
    const song = setlist.songs[index];
    setCurrentSong(index, song.bpm, song.time_signature);
  }, [resetBeat, setCurrentSong, setPlaying, setlist]);

  const goToSection = useCallback((index) => {
    setCurrentSection(index);
  }, [setCurrentSection]);

  // Keyboard controls
  useEffect(() => {
    const handleKeyDown = (e) => {
      switch(e.key) {
        case " ":
          e.preventDefault();
          togglePlayHandler();
          break;
        case "Escape":
          stopAndReset();
          break;
        case "ArrowUp":
          e.preventDefault();
          adjustBpm(e.shiftKey ? 1 : 5);
          break;
        case "ArrowDown":
          e.preventDefault();
          adjustBpm(e.shiftKey ? -1 : -5);
          break;
        case "ArrowRight":
          e.preventDefault();
          if (e.shiftKey && setlist) {
            goToSong(currentSongIndex + 1);
          } else if (currentSong?.sections?.length > 0) {
            goToSection(Math.min(currentSectionIndex + 1, currentSong.sections.length - 1));
          }
          break;
        case "ArrowLeft":
          e.preventDefault();
          if (e.shiftKey && setlist) {
            goToSong(currentSongIndex - 1);
          } else if (currentSong?.sections?.length > 0) {
            goToSection(Math.max(currentSectionIndex - 1, 0));
          }
          break;
        case "l":
        case "L":
          toggleLoop();
          break;
        case "t":
        case "T":
          handleTapTempo();
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    adjustBpm,
    currentSectionIndex,
    currentSong?.sections?.length,
    currentSongIndex,
    goToSection,
    goToSong,
    handleTapTempo,
    stopAndReset,
    toggleLoop,
    togglePlayHandler,
    setlist,
  ]);

  const formatTime = (ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  if (!setlist || !setlist.songs?.length) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center gap-4">
        <Music2 className="w-16 h-16 text-gray-700" />
        <p className="text-gray-500 text-xl">El setlist está vacío</p>
        <Link to={`/setlists/${id}`}>
          <Button variant="outline" className="border-white/20 text-white">
            Volver al setlist
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white select-none overflow-hidden" data-testid="rehearsal-page">
      {/* Countdown Overlay */}
      {showCountdown && (
        <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center">
          <div className="text-center">
            <div className="text-[200px] font-bold font-['JetBrains_Mono'] text-primary animate-pulse">
              {countdown}
            </div>
            <p className="text-2xl text-gray-400">Preparando...</p>
          </div>
        </div>
      )}

      {/* Top Bar */}
      <div className="fixed top-0 left-0 right-0 z-40 bg-black/80 backdrop-blur-sm border-b border-white/5">
        <div className="flex items-center justify-between px-6 h-14">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => { endSession(); navigate(`/setlists/${id}`); }}
              className="text-gray-400 hover:text-white"
              data-testid="exit-rehearsal-btn"
            >
              <X className="w-5 h-5" />
            </Button>
            <div>
              <span className="text-gray-500 font-medium">{setlist.name}</span>
              <span className="text-gray-600 mx-2">|</span>
              <span className="text-secondary font-medium">Modo Ensayo</span>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            {/* Session timer */}
            {sessionTime > 0 && (
              <div className="flex items-center gap-2 text-gray-500">
                <Clock className="w-4 h-4" />
                <span className="font-mono text-sm">{formatTime(sessionTime)}</span>
              </div>
            )}
            <span className="text-gray-500 font-mono">
              {currentSongIndex + 1} / {setlist.songs.length}
            </span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="min-h-screen flex flex-col pt-14 pb-56">
        {/* Song Info */}
        <div className="text-center pt-8 pb-4">
          <h1 className="text-3xl font-bold text-white font-['Manrope']" data-testid="current-song-title">
            {currentSong?.title}
          </h1>
          {currentSong?.artist && (
            <p className="text-lg text-gray-500 mt-1">{currentSong.artist}</p>
          )}
        </div>

        {/* Key Display */}
        <div className="text-center py-4">
          <Badge 
            className="text-4xl font-bold bg-primary/20 text-primary border-primary/30 px-8 py-4 font-['JetBrains_Mono']"
            data-testid="current-key-badge"
          >
            {currentKey}
          </Badge>
        </div>

        {/* Beat Visualizer - Enhanced */}
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            {/* Beat circles with visual hierarchy */}
            <div className="flex items-center justify-center gap-4 mb-8">
              {[...Array(beatsPerBar)].map((_, i) => {
                const isActive = isPlaying && beatInBar === i;
                const isDownbeat = i === 0;
                
                return (
                  <div
                    key={i}
                    className={`rounded-full border-4 transition-all duration-75 flex items-center justify-center ${
                      isDownbeat ? 'w-20 h-20' : 'w-14 h-14'
                    } ${
                      isActive
                        ? isDownbeat
                          ? "bg-secondary border-secondary scale-110 shadow-[0_0_40px_rgba(34,197,94,0.7)]"
                          : "bg-primary/80 border-primary scale-105 shadow-[0_0_20px_rgba(139,92,246,0.5)]"
                        : isDownbeat
                          ? "border-white/30 bg-white/5"
                          : "border-white/15 bg-transparent"
                    }`}
                    data-testid={`beat-indicator-${i}`}
                  >
                    <span className={`font-bold font-['JetBrains_Mono'] ${
                      isDownbeat ? 'text-2xl' : 'text-xl'
                    } ${
                      isActive ? "text-black" : isDownbeat ? "text-gray-400" : "text-gray-600"
                    }`}>
                      {i + 1}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* BPM Display with fine control */}
            <div className="mb-8">
              <div className="flex items-center justify-center gap-3">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => adjustBpm(-5)}
                  className="border-white/20 text-white h-10 w-10"
                  data-testid="bpm-decrease-btn"
                >
                  <Minus className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => adjustBpm(-1)}
                  className="text-gray-500 text-xs px-2"
                >
                  -1
                </Button>
                
                <div className="text-center min-w-[140px]">
                  <div 
                    className="text-6xl font-bold font-['JetBrains_Mono'] text-white"
                    data-testid="bpm-display"
                  >
                    {bpm}
                  </div>
                  <div className="text-sm text-gray-500 uppercase tracking-wider">BPM</div>
                </div>
                
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => adjustBpm(1)}
                  className="text-gray-500 text-xs px-2"
                >
                  +1
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => adjustBpm(5)}
                  className="border-white/20 text-white h-10 w-10"
                  data-testid="bpm-increase-btn"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              
              {/* BPM Slider */}
              <div className="mt-4 flex items-center justify-center gap-4">
                <span className="text-xs text-gray-600 w-8">30</span>
                <Slider
                  value={[bpm]}
                  onValueChange={(v) => setBpm(v[0])}
                  min={30}
                  max={280}
                  step={1}
                  className="w-64"
                />
                <span className="text-xs text-gray-600 w-8">280</span>
              </div>
            </div>

            {/* Tap Tempo Button */}
            <div className="mb-6">
              <Button
                variant="outline"
                size="lg"
                onClick={handleTapTempo}
                className="border-accent/50 text-accent hover:bg-accent/10 gap-2 px-6"
                data-testid="tap-tempo-btn"
              >
                <Hand className="w-5 h-5" />
                TAP TEMPO
              </Button>
              <p className="text-xs text-gray-600 mt-2">Presiona 4 veces al ritmo deseado (o tecla T)</p>
            </div>

            {/* Current Section */}
            {currentSection && (
              <div className="mb-6">
                <Badge 
                  className={`text-xl px-6 py-2 ${
                    isLooping
                      ? "bg-yellow-500/20 text-yellow-400 border-yellow-500/30"
                      : "bg-accent/20 text-accent border-accent/30"
                  }`}
                  data-testid="current-section-badge"
                >
                  {isLooping && "LOOP "}
                  {currentSection.name}
                  {currentSection.bars && ` | ${currentSection.bars} compases`}
                </Badge>
                {currentSection.notes && (
                  <p className="text-gray-500 mt-2 text-sm max-w-md mx-auto">{currentSection.notes}</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Sections Navigation */}
        {currentSong?.sections?.length > 0 && (
          <div className="px-6 pb-4">
            <div className="flex items-center justify-center gap-2 overflow-x-auto py-2">
              {currentSong.sections.map((section, i) => (
                <button
                  key={i}
                  onClick={() => goToSection(i)}
                  className={`px-4 py-2 rounded-lg transition-all text-sm font-medium ${
                    i === currentSectionIndex
                      ? loopSectionIndex === i
                        ? "bg-yellow-500/20 text-yellow-400 ring-2 ring-yellow-500"
                        : "bg-primary text-white"
                      : "bg-white/5 text-gray-400 hover:bg-white/10"
                  }`}
                  data-testid={`section-btn-${i}`}
                >
                  {section.name}
                  {section.bars && <span className="ml-1 opacity-60">({section.bars})</span>}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Bottom Controls */}
      <div className="fixed bottom-0 left-0 right-0 bg-black/95 backdrop-blur-sm border-t border-white/10">
        {/* Song Navigation */}
        <div className="flex items-center justify-center gap-2 py-3 border-b border-white/5 overflow-x-auto px-6">
          {setlist.songs.map((song, i) => (
            <button
              key={i}
              onClick={() => goToSong(i)}
              className={`flex-shrink-0 px-3 py-1.5 rounded-lg transition-all text-sm ${
                i === currentSongIndex
                  ? "bg-primary text-white"
                  : "bg-white/5 text-gray-400 hover:bg-white/10"
              }`}
              data-testid={`song-nav-btn-${i}`}
            >
              {i + 1}. {song.title?.length > 12 ? `${song.title.substring(0, 12)}...` : song.title}
            </button>
          ))}
        </div>

        {/* Main Controls */}
        <div className="flex items-center justify-between px-6 py-4">
          <Button
            onClick={() => goToSong(currentSongIndex - 1)}
            disabled={currentSongIndex === 0}
            variant="outline"
            size="lg"
            className="border-white/20 text-white gap-2"
            data-testid="prev-song-btn"
          >
            <ChevronLeft className="w-5 h-5" />
            Anterior
          </Button>

          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="icon"
              onClick={stopAndReset}
              className="border-white/20 text-white h-12 w-12"
              data-testid="stop-btn"
            >
              <RotateCcw className="w-5 h-5" />
            </Button>
            
            <Button
              size="lg"
              onClick={togglePlayHandler}
              className={`h-16 w-16 rounded-full ${
                isPlaying 
                  ? "bg-red-500 hover:bg-red-600" 
                  : "bg-secondary hover:bg-secondary/90"
              }`}
              data-testid="play-pause-btn"
            >
              {isPlaying ? (
                <Pause className="w-8 h-8" />
              ) : (
                <Play className="w-8 h-8 ml-1" />
              )}
            </Button>
            
            <Button
              variant="outline"
              size="icon"
              onClick={toggleLoop}
              className={`h-12 w-12 ${
                isLooping 
                  ? "border-yellow-500 text-yellow-400 bg-yellow-500/10" 
                  : "border-white/20 text-white"
              }`}
              data-testid="loop-btn"
            >
              <Repeat className="w-5 h-5" />
            </Button>
          </div>

          <Button
            onClick={() => goToSong(currentSongIndex + 1)}
            disabled={currentSongIndex === setlist.songs.length - 1}
            size="lg"
            className="bg-primary hover:bg-primary/90 gap-2"
            data-testid="next-song-btn"
          >
            Siguiente
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>

        {/* Volume Control */}
        <div className="flex items-center justify-center gap-4 pb-4 px-6">
          <Volume2 className="w-4 h-4 text-gray-500" />
          <Slider
            value={[volume]}
            onValueChange={(v) => setVolume(v[0])}
            max={100}
            step={5}
            className="w-48"
          />
          <span className="text-sm text-gray-500 w-10">{volume}%</span>
        </div>

        {/* Keyboard hints */}
        <div className="text-center pb-3 text-xs text-gray-700">
          ESPACIO play/pause | ↑↓ BPM (±5) | SHIFT+↑↓ BPM (±1) | ←→ secciones | SHIFT+←→ canciones | L loop | T tap tempo | ESC detener
        </div>
      </div>
    </div>
  );
};

export default RehearsalPage;
